import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, Share, ImageBackground } from 'react-native';
import { Image } from 'expo-image';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Svg, Path, Circle, Rect } from 'react-native-svg';
import * as WebBrowser from 'expo-web-browser';
import PadrinhoEloLogo from '../components/PadrinhoEloLogo';
import SobrietyClockCard from '../components/SobrietyClockCard';
import MeetingsCarousel from '../components/MeetingsCarousel';
import { useSobriety } from '@/contexts/SobrietyContext';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import Avatar from '../components/Avatar';
import SentimentCalendar from '../components/SentimentCalendar';
import { fetchUserClinic } from '../services/clinicsApi';
import { preloadCarouselImages } from '../components/CachedImage';

interface ClinicData {
  id?: string;
  name: string;
  logo_path?: string;
  description?: string;
}

export default function MenuModal() {
  const router = useRouter();
  const { t } = useLanguage();
  const { sobrietyState } = useSobriety();
  const { userProfile } = useAuth();
  const [clinicData, setClinicData] = useState<ClinicData | null>(null);

  useEffect(() => {
    const loadClinicData = async () => {
      try {
        console.log('🏥 Loading clinic data for user...');
        const data = await fetchUserClinic();
        console.log('🏥 Clinic data loaded:', data);

        if (data) {
          setClinicData({
            id: data.id?.toString(),
            name: data.name,
            logo_path: data.logo_path,
            description: data.description
          });
        } else {
          console.log('🏥 No clinic found for user');
          setClinicData({ name: t('menu.providerNotFound') });
        }
      } catch (error) {
        console.error("🏥 Failed to fetch clinic data:", error);
        // Handle error appropriately, e.g., set default placeholder
        setClinicData({ name: t('menu.providerNotFound') });
      }
    };

    // Only load if we have a user profile
    if (userProfile?.id) {
      loadClinicData();
    }
  }, [userProfile?.id]);

  // Preload carousel images when component mounts
  useEffect(() => {
    preloadCarouselImages();
  }, []);

  // Function to get first name from profile data
  const getFirstName = () => {
    if (userProfile?.full_name) {
      return userProfile.full_name.split(' ')[0];
    }
    return t('menu.defaultUserName');
  };

  const handleSobrietyClockPress = () => {
    router.dismiss();
    router.push('/sobriety-clock');
  };

  const handleClose = () => {
    router.dismiss();
  };

  const handleEncontrosOnlinePress = () => {
    router.dismiss();
    router.push('/encontros-online');
  };

  const handleProfilePress = () => {
    router.dismiss();
    router.push('/perfil');
  };

  const handleDiaryPress = () => {
    router.dismiss();
    router.push('/diary');
  };

  // Function to render the provider logo or name
  const renderProviderLogo = () => {
    if (clinicData?.logo_path) {
      // Validate URL format as recommended by Elo Web team
      if (!clinicData.logo_path.includes('supabase.co')) {
        console.warn('🏥 Invalid logo URL format:', clinicData.logo_path);
        // Fallback to provider name text
        return (
          <Text style={styles.providerNameText}>
            {clinicData?.name || t('menu.provider')}
          </Text>
        );
      }

      console.log('🏥 Using direct Supabase URL from database:', clinicData.logo_path);

      return (
        <Image
          source={{ uri: clinicData.logo_path }}
          style={styles.providerLogoImage}
          contentFit="contain"
        />
      );
    }

    console.log('🏥 No logo_path found, using provider name text');

    // Fallback to provider name text
    return (
      <Text style={styles.providerNameText}>
        {clinicData?.name || t('menu.provider')}
      </Text>
    );
  };

  return (
    <View style={styles.container}>
      {/* Fixed Header */}
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <Text style={styles.headerTitle}>Menu</Text>
          <TouchableOpacity style={styles.closeButton} onPress={handleClose}>
            <Ionicons name="close" size={24} color="#151515" />
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false} contentContainerStyle={styles.scrollContent}>

        {/* Greeting Section */}
        <View style={styles.greetingSection}>
          <TouchableOpacity style={styles.greetingContainer} onPress={handleProfilePress} activeOpacity={0.8}>
            <View style={styles.greetingTextContainer}>
              <Text style={styles.greetingHello}>{t('menu.greeting')}</Text>
              <Text style={styles.greetingName}>{getFirstName()} 👋</Text>
            </View>
            <Avatar
              uri={userProfile?.avatar_url}
              size={40}
              showInitials={true}
              initials={userProfile?.full_name || undefined}
              loading={false}
            />
          </TouchableOpacity>
        </View>

        {/* Sobriety Clock Card */}
        <View style={styles.galleryContainer}>
          <TouchableOpacity onPress={handleSobrietyClockPress} activeOpacity={0.8}>
            <SobrietyClockCard />
          </TouchableOpacity>
        </View>

        {/* Diário Card */}
        <View style={styles.anotacoesSection}>
          <TouchableOpacity style={styles.anotacoesCard} onPress={handleDiaryPress}>
            <View style={styles.anotacoesIcon}>
              <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <Path d="M2.91797 17.2197C2.91797 17.2197 3.32652 6.94637 14.2295 3.19531C23.5679 0.706427 12.2244 10.3887 12.2244 10.3887L7.55469 10.8472L9.72159 12.5625C7.59532 14.0573 5.46972 14.7962 3.34297 14.7767" stroke="#56554D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <Path opacity="0.4" d="M9.34766 17.2197H14.6893" stroke="#56554D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </Svg>
            </View>
            <View style={styles.anotacoesContent}>
              <Text style={styles.anotacoesTitle}>{t('menu.diary')}</Text>
              <Text style={styles.anotacoesSubtitle}>{t('menu.diarySubtitle')}</Text>
            </View>
          </TouchableOpacity>
        </View>



        {/* Sentiment Calendar Section */}
        <SentimentCalendar />

        {/* Meetings Carousel */}
        <MeetingsCarousel />

        {/* Mais do Padrinho Section */}
        <View style={styles.maisDoPadrinhoSection}>
          <Text style={styles.sectionTitle}>{t('menu.moreContent')}</Text>

          <ScrollView
            horizontal
            pagingEnabled
            showsHorizontalScrollIndicator={false}
            style={styles.carouselScroll}
            contentContainerStyle={styles.carouselContainer}
            snapToInterval={345 + 8}
            decelerationRate="fast"
            snapToAlignment="start"
          >
            {/* Community Card */}
            <TouchableOpacity
              onPress={() => WebBrowser.openBrowserAsync('https://chat.whatsapp.com/CEuzR6F7MCI648Fi90adKs', {
                dismissButtonStyle: 'close',
                presentationStyle: WebBrowser.WebBrowserPresentationStyle.CURRENT_CONTEXT,
                controlsColor: '#7E7D75',
                toolbarColor: '#FFFFFF',
              })}
            >
              <ImageBackground
                source={require('../assets/images/community.png')}
                style={styles.carouselCard}
                resizeMode="cover"
                imageStyle={{ borderRadius: 12 }}
              >
                <View style={styles.cardContent}>
                  <View style={styles.cardHeader}>
                    <View style={styles.cardIcon}>
                      <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <Path d="M16.3887 6.8967C17.7837 6.7007 18.8577 5.5047 18.8607 4.0557C18.8607 2.6277 17.8197 1.4437 16.4547 1.2197" stroke="#015480" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <Path d="M18.2285 10.2503C19.5795 10.4523 20.5225 10.9253 20.5225 11.9003C20.5225 12.5713 20.0785 13.0073 19.3605 13.2813" stroke="#015480" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <Path fillRule="evenodd" clipRule="evenodd" d="M10.3867 10.6638C7.17273 10.6638 4.42773 11.1508 4.42773 13.0958C4.42773 15.0398 7.15573 15.5408 10.3867 15.5408C13.6007 15.5408 16.3447 15.0588 16.3447 13.1128C16.3447 11.1668 13.6177 10.6638 10.3867 10.6638Z" stroke="#015480" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <Path fillRule="evenodd" clipRule="evenodd" d="M10.3874 7.8879C12.4964 7.8879 14.2064 6.1789 14.2064 4.0689C14.2064 1.9599 12.4964 0.249901 10.3874 0.249901C8.27842 0.249901 6.56839 1.9599 6.56839 4.0689C6.56042 6.1709 8.25742 7.8809 10.3594 7.8879H10.3874Z" stroke="#015480" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <Path d="M4.38411 6.8967C2.98811 6.7007 1.91511 5.5047 1.91211 4.0557C1.91211 2.6277 2.95311 1.4437 4.31811 1.2197" stroke="#015480" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <Path d="M2.544 10.2503C1.193 10.4523 0.25 10.9253 0.25 11.9003C0.25 12.5713 0.694 13.0073 1.412 13.2813" stroke="#015480" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </Svg>
                    </View>
                    <Text style={styles.cardTag}>{t('menu.padrinho.communityTitle')}</Text>
                  </View>
                  <Text style={styles.cardTitle}>{t('menu.padrinho.communityDescription')}</Text>
                  <Text style={styles.cardSubtitle}>{t('menu.padrinho.communityAction')}</Text>
                </View>
              </ImageBackground>
            </TouchableOpacity>

            {/* Pé na Trilha Card */}
            <TouchableOpacity
              onPress={() => WebBrowser.openBrowserAsync('https://open.spotify.com/show/2xzYogycLrTYvIye5fzQBj?si=MVyGLKWNQ9qzzdpjrqjbSw', {
                dismissButtonStyle: 'close',
                presentationStyle: WebBrowser.WebBrowserPresentationStyle.CURRENT_CONTEXT,
                controlsColor: '#7E7D75',
                toolbarColor: '#FFFFFF',
              })}
            >
              <ImageBackground
                source={require('../assets/images/podcast.png')}
                style={styles.carouselCard}
                resizeMode="cover"
                imageStyle={{ borderRadius: 12 }}
              >
                <View style={styles.cardContent}>
                  <View style={styles.cardHeader}>
                    <View style={styles.cardIcon}>
                      <Svg width="25" height="24" viewBox="0 0 25 24" fill="none">
                        <Rect x="3.5" y="7" width="2" height="10" rx="1" fill="#015480"/>
                        <Rect x="7.5" y="9" width="2" height="6" rx="1" fill="#015480"/>
                        <Rect x="11.5" y="7" width="2" height="10" rx="1" fill="#015480"/>
                        <Rect x="15.5" y="5" width="2" height="14" rx="1" fill="#015480"/>
                        <Rect x="19.5" y="9" width="2" height="6" rx="1" fill="#015480"/>
                      </Svg>
                    </View>
                    <Text style={styles.cardTag}>{t('menu.padrinho.podcastTitle')}</Text>
                  </View>
                  <Text style={styles.cardTitle}>{t('menu.padrinho.podcastDescription')}</Text>
                  <Text style={styles.cardSubtitle}>{t('menu.padrinho.podcastAction')}</Text>
                </View>
              </ImageBackground>
            </TouchableOpacity>

            {/* Bússola Card */}
            <TouchableOpacity
              onPress={() => WebBrowser.openBrowserAsync('https://www.padrinho.app/blog/home', {
                dismissButtonStyle: 'close',
                presentationStyle: WebBrowser.WebBrowserPresentationStyle.CURRENT_CONTEXT,
                controlsColor: '#7E7D75',
                toolbarColor: '#FFFFFF',
              })}
            >
              <ImageBackground
                source={require('../assets/images/blog.png')}
                style={styles.carouselCard}
                resizeMode="cover"
                imageStyle={{ borderRadius: 12 }}
              >
                <View style={styles.cardContent}>
                  <View style={styles.cardHeader}>
                    <View style={styles.cardIcon}>
                      <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <Path d="M21.1396 11.7582C21.1396 6.9875 17.272 3.11906 12.5004 3.11906C7.72884 3.11906 3.86133 6.9875 3.86133 11.7582C3.86133 16.5298 7.72884 20.3972 12.5004 20.3972C17.272 20.3972 21.1396 16.5298 21.1396 11.7582Z" stroke="#015480" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <Path d="M12.5002 3.11906V4.75629M21.1394 11.7582H19.5013M12.5002 20.3972V18.761M5.4966 11.7582H3.85938" stroke="#015480" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <Path fillRule="evenodd" clipRule="evenodd" d="M9.49609 14.7615L10.9278 10.187L15.5023 8.75532L14.0706 13.3298L9.49609 14.7615Z" stroke="#015480" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </Svg>
                    </View>
                    <Text style={styles.cardTag}>{t('menu.padrinho.blogTitle')}</Text>
                  </View>
                  <Text style={styles.cardTitle}>{t('menu.padrinho.blogDescription')}</Text>
                  <Text style={styles.cardSubtitle}>{t('menu.padrinho.blogAction')}</Text>
                </View>
              </ImageBackground>
            </TouchableOpacity>
          </ScrollView>
        </View>

        {/* Convide para o Padrinho Section */}
        <View style={styles.conviteParaPadrinhoSection}>
          <TouchableOpacity
            style={styles.conviteParaPadrinhoCard}
            onPress={async () => {
              try {
                const message = t('menu.shareMessage');
                await Share.share({
                  message: message,
                  title: t('menu.shareTitle'),
                });
              } catch (error) {
                console.error('Erro ao compartilhar:', error);
              }
            }}
          >
            <View style={styles.conviteIcon}>
              <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <Path d="M0 10C0.00011 4.21636 5.26639 -0.396253 11.2369 0.723752C15.0001 1.42987 18.0146 4.44565 18.7207 8.20941C19.8398 14.1781 15.2281 19.4445 9.44444 19.4444C7.9252 19.4444 6.41038 19.0926 5.07543 18.4537C4.29185 18.6296 3.11121 18.9188 2.25498 19.1342C0.951631 19.4623 -0.0220025 18.3014 0.282118 16.9502C0.495583 16.0021 0.787435 14.7244 0.934311 14.0444C0.358374 12.8193 0 11.4573 0 10ZM8.20849 6.76217C7.79146 6.15202 6.93313 6.07439 6.41823 6.58825L6.29632 6.70902C4.95782 8.01316 5.98276 10.0627 7.69746 11.7741C9.41305 13.4857 11.4269 14.4593 12.7281 13.1424L12.8477 13.0239C13.3547 12.5179 13.2771 11.6594 12.6678 11.2426C12.472 11.1056 12.269 10.9601 12.0548 10.8071C11.5781 10.4772 10.9341 10.527 10.5609 10.9279L10.3428 11.1448C9.39501 10.5856 8.92623 10.0485 8.29623 9.08279L8.51235 8.84688C8.91346 8.44577 8.9630548 7.80548 8.63348 7.32845C8.48025 7.11358 8.33578 6.91135 8.20849 6.76217Z" fill="#DAD8CF"/>
              </Svg>
            </View>
            <View style={styles.conviteContent}>
              <Text style={styles.conviteTitle}>{t('menu.inviteTitle')}</Text>
              <Text style={styles.conviteSubtitle}>{t('menu.inviteSubtitle')}</Text>
            </View>
          </TouchableOpacity>
        </View>

        {/* Provider Section */}
        <View style={styles.providerSection}>
          <TouchableOpacity style={styles.providerCard} onPress={() => router.push('/provider')}>
            <View style={styles.providerHeader}>
              {renderProviderLogo()}
              <Ionicons name="chevron-forward" size={16} color="#151515" />
            </View>
            <Text style={styles.providerDescription}>
              {t('menu.providerDescription')}
            </Text>
          </TouchableOpacity>
        </View>

      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9F8F3',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 24,
  },
  header: {
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F0EEE4',
    backgroundColor: '#F9F8F3',
    zIndex: 1000,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  headerTitle: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#151515',
  },
  closeButton: {
    position: 'absolute',
    right: 0,
    padding: 4,
  },
  greetingSection: {
    paddingHorizontal: 24,
    paddingVertical: 24,
  },
  greetingContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
  },
  greetingTextContainer: {
    flex: 1,
  },
  greetingHello: {
    fontSize: 32,
    fontFamily: 'Sentient-Regular',
    color: '#A19F97',
    lineHeight: 36,
  },
  greetingName: {
    fontSize: 32,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    lineHeight: 36,
  },
  galleryContainer: {
    paddingHorizontal: 24,
    marginBottom: 16,
  },
  anotacoesSection: {
    paddingHorizontal: 24,
    marginBottom: 12,
  },
  anotacoesCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    gap: 16,
  },
  anotacoesIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: '#F0EEE4',
    justifyContent: 'center',
    alignItems: 'center',
  },
  anotacoesContent: {
    flex: 1,
    gap: 4,
  },
  anotacoesTitle: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#151515',
  },
  anotacoesSubtitle: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#757C85',
  },
  chevronContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuSection: {
    paddingHorizontal: 24,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    gap: 16,
  },

  menuItemIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: '#7E7D75',
    justifyContent: 'center',
    alignItems: 'center',
  },

  menuItemContent: {
    flex: 1,
    gap: 4,
  },
  menuItemTitle: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#151515',
  },
  menuItemSubtitle: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#757C85',
  },
  sectionTitle: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#151515',
  },
  providerSection: {
    paddingHorizontal: 24,
    paddingTop: 15,
    marginBottom: 24,
  },
  providerTitle: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#151515',
    marginBottom: 16,
  },
  providerCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    padding: 24,
    gap: 16,
  },
  providerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  providerDescription: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#151515',
    lineHeight: 19.6,
  },
  providerLogoImage: {
    width: 140,
    height: undefined,
    minHeight: 40,
    resizeMode: 'contain',
    alignSelf: 'flex-start',
  },
  providerNameText: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    maxWidth: 147,
    flex: 1,
  },
  providerLogoPlaceholderText: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#757C85',
  },

  maisDoPadrinhoSection: {
    paddingHorizontal: 24,
    marginBottom: 16,
    gap: 16,
  },
  carouselScroll: {
    marginHorizontal: -24,
  },
  carouselContainer: {
    paddingLeft: 24,
    paddingRight: 24,
  },
  carouselCard: {
    width: 345,
    height: 200,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#C4C2BA',
    overflow: 'hidden',
    marginRight: 8,
  },
  cardContent: {
    flex: 1,
    padding: 24,
    gap: 12,
    maxWidth: '65%',
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  cardIcon: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardTag: {
    fontSize: 12,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#015480',
    flex: 1,
  },
  cardTitle: {
    fontSize:16,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    lineHeight: 22.4,
    flex: 1,
  },
  cardSubtitle: {
    fontSize: 12,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#7E7D75',
  },
  conviteParaPadrinhoSection: {
    paddingHorizontal: 24,
  },
  conviteParaPadrinhoCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#F0EEE4',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#C4C2BA',
    gap: 16,
    height: 72,
  },
  conviteIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: '#3D3C38',
    justifyContent: 'center',
    alignItems: 'center',
  },
  conviteContent: {
    flex: 1,
    gap: 4,
    justifyContent: 'center',
  },
  conviteTitle: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#151515',
    lineHeight: 19.6,
  },
  conviteSubtitle: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#757C85',
    lineHeight: 12,
  },
});